import { createTag, listTags } from './entity-api/tag-api.js';
import { ServerError } from './../shared/errors.js';

/**
 * Split a comma-joined tag string into an array of trimmed names.
 * Robust to undefined/null and empty segments.
 * @param {string | null | undefined} value
 * @returns {string[]}
 */
export function splitTagNames(value) {
	if (!value) { return []; }
	return String(value).split(',').map((s) => s.trim()).filter(Boolean);
}

/**
 * Join an array of names into the comma-joined form used by the form value.
 * @param {string[]} names
 * @returns {string}
 */
export function joinTagNames(names) {
	return names.join(', ');
}

/**
 * Resolve an array of tag IDs to their display names, using the loaded tag list.
 * Unknown IDs (e.g. schedule free-text tags) are kept as-is.
 * @param {string[]} ids
 * @param {Array<{ id: string, name: string }>} tags
 * @returns {string}
 */
export function resolveTagIdsToNames(ids, tags) {
	return joinTagNames((ids ?? []).map((id) => {
		const tag = (tags ?? []).find((t) => t.id === id);
		return tag ? tag.name : id;
	}));
}

/**
 * Resolve an array of tag names to their IDs, creating missing tags on the fly.
 * If creating a tag races with an existing tag (HTTP 409), re-lists the tag
 * library and resolves by name instead of failing.
 *
 * @param {string[]} names
 * @param {Array<{ id: string, name: string }>} tags
 * @param {(path: string, options?: { method?: string, body?: string }) => Promise<any>} request
 * @returns {Promise<string[]>}
 */
export async function resolveTagNamesToIds(names, tags, request) {
	const known = [...(tags ?? [])];
	const ids = [];
	for (const name of names) {
		const existing = known.find((t) => t.name === name);
		if (existing) { ids.push(existing.id); continue; }
		try {
			const created = await createTag(request, { name });
			known.push({ id: created.id, name });
			ids.push(created.id);
		} catch (err) {
			if (err instanceof ServerError && err.statusCode === 409) {
				const fresh = await listTags(request);
				const tag = fresh.find((t) => t.name === name);
				if (tag) {
					known.push({ id: tag.id, name });
					ids.push(tag.id);
					continue;
				}
			}
			throw err;
		}
	}
	return ids;
}
